import type { ReactNode } from 'react'
import { useEffect, useId, useState } from 'react'
import type { IRect } from 'react-confetti/src/Rect'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import { useMutation as useConnectMutation } from '@connectrpc/connect-query'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import {
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import type { Stripe, StripeCardNumberElement } from '@stripe/stripe-js'
import { useMutation } from '@tanstack/react-query'

import type { PlaceOrderRequest } from '@jotta/grpc-connect-openapi/payment'
import {
  type PlaceOrderResponse_Vipps,
  RecurringSubscriptionInterval,
} from '@jotta/grpc-connect-openapi/payment'
import { SubscriptionInterval } from '@jotta/grpc-connect-openapi/payment'
import {
  checkAndConfirmStripe as checkAndConfirmStripeDescriptor,
  placeOrder as placeOrderDescriptor,
} from '@jotta/grpc-connect-openapi/subscriptionQuery'
import { useLocale } from '@jotta/i18n'
import { useAndConsumeSearchParam } from '@jotta/router'
import { StripeElementsContainer } from '@jotta/settings/StripeContainer'
import { AppError } from '@jotta/types/AppError'
import type { MessageData } from '@jotta/types/compat'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@jotta/ui/Accordion'
import { PlainBrandIcon } from '@jotta/ui/BrandIcon'
import type { ButtonProps } from '@jotta/ui/Button'
import { Button } from '@jotta/ui/Button'
import { Confetti, useConfettiState } from '@jotta/ui/Confetti'
import { Divider } from '@jotta/ui/Divider'
import { ErrorMessage } from '@jotta/ui/ErrorMessage'
import { Headline3 } from '@jotta/ui/Headline'
import {
  FormFieldErrorMessage,
  FormFieldGoodMessage,
  Label,
} from '@jotta/ui/Label'
import { Modal } from '@jotta/ui/Modal'
import { PaymentAccordion } from '@jotta/ui/PaymentAccordion'
import {
  PaymentMethodOption,
  paymentMethodOptions,
} from '@jotta/ui/paymentMethodOptions'
import { PaymentRadio } from '@jotta/ui/PaymentRadio'
import { SelectCountry } from '@jotta/ui/Select'
import type { StripeValidationError } from '@jotta/ui/Stripe'
import { StripeCardForm } from '@jotta/ui/Stripe'
import SvgMinus from '@jotta/ui/SvgMinus'
import SvgPlus from '@jotta/ui/SvgPlus'
import SvgX from '@jotta/ui/SvgX'
import { VoucherDialog } from '@jotta/ui/VoucherDialog'
import { cn } from '@jotta/utils/css'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import type { Money } from '@jotta/utils/formatPrice'
import { formatPriceConnectWithInterval } from '@jotta/utils/formatPrice'
import { redirectAndWait } from '@jotta/utils/redirect'

import { CTAButton } from '../auth/CTAButton'

import type { OfferState } from './useOfferState'

function mapMoney(
  money: Money | undefined,
  fn: (amount: bigint) => bigint,
): Money | undefined {
  return money
    ? {
        currency: money.currency,
        amount: fn(BigInt(money.amount)),
      }
    : undefined
}

const paymentErrorParam = 'payment-error'

function mapInterval(
  interval: RecurringSubscriptionInterval,
): SubscriptionInterval {
  switch (interval) {
    case RecurringSubscriptionInterval.NOT_SET_RECURRING:
      return SubscriptionInterval.NOT_SET
    case RecurringSubscriptionInterval.MONTHLY_RECURRING:
      return SubscriptionInterval.MONTHLY
    case RecurringSubscriptionInterval.YEARLY_RECURRING:
      return SubscriptionInterval.YEARLY
    default:
      return exhaustiveGuard(interval)
  }
}

function placeOrderParams(
  offerState: OfferState,
): MessageData<PlaceOrderRequest> {
  const { productCode, interval, extraStorageTB, extraSeats, voucherCode } =
    offerState
  return {
    extraStorageTb: extraStorageTB,
    extraUserSeats: extraSeats,
    productCode,
    voucherCode,
    subscriptionInterval: mapInterval(interval), // TODO adjust api?
  }
}

function usePlaceOrderStripe(
  successUrl: string,
  failureUrl: string,
  offer: OfferState,
) {
  const { mutateAsync: placeOrder } = useConnectMutation(placeOrderDescriptor, {
    throwOnError: false,
  })
  const checkAndConfirmStripe = useCheckAndConfirmStripe(successUrl)
  const successPath = successUrl.startsWith(location.origin)
    ? successUrl.substring(location.origin.length)
    : '/web/billing'
  const failurePath = successUrl.startsWith(location.origin)
    ? failureUrl.substring(location.origin.length)
    : window.location.pathname + window.location.search

  return async (stripe: Stripe, newPaymentMethodId?: string) => {
    const placeOrderResponse = await placeOrder({
      ...placeOrderParams(offer),
      type: {
        case: 'stripe',
        value: {
          newPaymentMethodId: newPaymentMethodId,
          redirectHostname: location.host,
          clientState: {
            successPath: successPath,
            failurePath: failurePath,
          },
        },
      },
    })
    const placeOrderResponseType = placeOrderResponse.type
    switch (placeOrderResponseType.case) {
      case undefined:
      case 'vipps':
        throw new Error('invalid api response')
      case 'stripe':
        return await checkAndConfirmStripe(
          stripe,
          placeOrderResponseType.value.orderNumber,
        )
      case 'rejected':
        throw new AppError({
          view: placeOrderResponseType.value.message,
        })
      default:
        return exhaustiveGuard(placeOrderResponseType)
    }
  }
}

export function useCheckAndConfirmStripe(successUrl: string) {
  const { mutateAsync: checkAndConfirmStripe } = useConnectMutation(
    checkAndConfirmStripeDescriptor,
    {
      throwOnError: false,
    },
  )

  return async (stripe: Stripe, orderNumber: string) => {
    for (let i = 0; i < 2; i++) {
      const checkAndConfirmStripeResponse = await checkAndConfirmStripe({
        orderNumber: orderNumber,
      })
      const status = checkAndConfirmStripeResponse.status
      switch (status.case) {
        case 'success':
          return await redirectAndWait(successUrl)
        case 'cardActionRequired':
          await stripe.handleCardAction(status.value.clientSecret)
          break
        case 'cardSetupRequired':
          await stripe.confirmCardSetup(status.value.clientSecret)
          break
        case 'rejected':
          throw new AppError({
            view: status.value.message,
          })
        case 'redirectRequired':
          return await redirectAndWait(status.value.url)
        case undefined:
          throw new Error('invalid server response')
        default:
          return exhaustiveGuard(status)
      }
    }

    // max 2 attempts before failing
    throw new Error('Stripe payment failed')
  }
}

function CTA({
  offerState,
  summary,
  ...rest
}: {
  offerState: OfferState
  summary: ReactNode
} & ButtonProps) {
  const { isValid, isRefetching } = offerState
  return (
    <>
      <VoucherCodeInput offerState={offerState} />
      {summary}
      <CTAButton
        className="h-11 text-[16px]"
        {...rest}
        disabled={rest.disabled || !isValid || isRefetching}
      />
    </>
  )
}

type StripeType =
  | PaymentMethodOption.STRIPE_CARD
  | PaymentMethodOption.STRIPE_PAYPAL

function StripePayment({
  summary,
  offer,
  type,
  successUrl,
  failureUrl,
  useCurrent,
}: {
  summary: ReactNode
  offer: OfferState
  type: StripeType
  successUrl: string
  failureUrl: string
  useCurrent: boolean
}) {
  const elements = useElements()
  const stripe = useStripe()
  const [errors, setErrors] = useState<StripeValidationError>({})

  const placeOrderStripe = usePlaceOrderStripe(successUrl, failureUrl, offer)

  const {
    mutate: onVerify,
    data: stripeError,
    isPending,
    error,
    reset,
  } = useMutation({
    throwOnError: false,
    mutationFn: async (cardNumberElement: StripeCardNumberElement | null) => {
      if (!stripe) {
        throw new Error('stripe not initialized')
      }

      if (useCurrent) {
        return await placeOrderStripe(stripe, undefined)
      } else if (cardNumberElement) {
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardNumberElement,
        })
        if (error) {
          return error
        }
        return await placeOrderStripe(stripe, paymentMethod.id)
      } else if (type === PaymentMethodOption.STRIPE_PAYPAL) {
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'paypal',
        })
        if (error) {
          return error
        }
        return await placeOrderStripe(stripe, paymentMethod.id)
      }
    },
  })

  useEffect(() => {
    reset()
  }, [reset, offer])

  return (
    <>
      {type === PaymentMethodOption.STRIPE_CARD && !useCurrent && (
        <StripeCardForm
          onVerify={onVerify}
          errors={errors}
          setErrors={setErrors}
          elements={elements}
          className="pt-6"
        />
      )}
      <CTA
        offerState={offer}
        summary={summary}
        loading={isPending}
        disabled={(!useCurrent && !elements) || !stripe}
        onClick={e => {
          e.preventDefault()
          if (useCurrent) {
            onVerify(null)
          } else if (elements) {
            onVerify(elements.getElement(CardNumberElement))
          }
        }}
      >
        <Trans>Confirm purchase</Trans>
      </CTA>

      <ErrorMessage marginTop>{stripeError?.message}</ErrorMessage>
      <ErrorMessage marginTop error={error} />
    </>
  )
}

function VippsPayment({
  summary,
  offer,
  successUrl,
  failureUrl,
}: {
  summary: ReactNode
  offer: OfferState
  successUrl: string
  failureUrl: string
}) {
  const {
    mutate: placeOrder,
    error,
    isPending,
  } = useConnectMutation(placeOrderDescriptor, {
    onSuccess: data => {
      const { confirmationUrl } = data.type.value as PlaceOrderResponse_Vipps
      return redirectAndWait(confirmationUrl)
    },
    throwOnError: false,
  })

  return (
    <>
      <CTA
        offerState={offer}
        summary={summary}
        loading={isPending}
        onClick={e => {
          e.preventDefault()
          placeOrder({
            ...placeOrderParams(offer),
            type: {
              case: 'vipps',
              value: {
                redirectUrl: successUrl,
                failureRedirectUrl: failureUrl,
              },
            },
          })
        }}
      >
        <Trans>Confirm purchase</Trans>
      </CTA>
      <ErrorMessage marginTop error={error} />
    </>
  )
}

function PaymentType({
  summary,
  type,
  offerState,
  successUrl,
  useCurrent,
}: {
  summary: ReactNode
  type: PaymentMethodOption
  offerState: OfferState
  successUrl: string
  useCurrent: boolean
}) {
  const failureUrl = new URL(window.location.href)
  failureUrl.searchParams.set(paymentErrorParam, 'true')
  switch (type) {
    case PaymentMethodOption.STRIPE_CARD:
    case PaymentMethodOption.STRIPE_PAYPAL:
      return (
        <StripeElementsContainer
          publicKey={offerState.paymentMethods
            ?.map(a =>
              a.type.case === 'stripe'
                ? a.type.value.publishableKey
                : undefined,
            )
            ?.at(0)}
        >
          <StripePayment
            offer={offerState}
            type={type}
            summary={summary}
            successUrl={successUrl}
            failureUrl={failureUrl.href}
            useCurrent={useCurrent}
          />
        </StripeElementsContainer>
      )
    case PaymentMethodOption.VIPPS:
      return (
        <VippsPayment
          offer={offerState}
          summary={summary}
          successUrl={successUrl}
          failureUrl={failureUrl.href}
        />
      )
    default:
      return exhaustiveGuard(type)
  }
}

function PaymentPlan({
  offerState,
  interval,
}: {
  offerState: OfferState
  interval: RecurringSubscriptionInterval
}) {
  const locale = useLocale()
  const { offer, interval: offerInterval, setInterval } = offerState
  const checked = offerInterval == interval
  const yearlyBasePrice = offer?.yearly?.basePrice
  const monthlyBasePrice = offer?.monthly?.basePrice
  const y = yearlyBasePrice?.amount
  const m = monthlyBasePrice?.amount
  const yearlyDiscount = y && m && 100n - (100n * y) / (m * 12n)

  const { label, discountPercent, yearlyPrice, monthlyPrice } = (() => {
    switch (interval) {
      case RecurringSubscriptionInterval.YEARLY_RECURRING: {
        return {
          label: t`Billed yearly`,
          discountPercent: yearlyDiscount ? `${yearlyDiscount}%` : undefined,
          yearlyPrice: yearlyBasePrice,
          monthlyPrice: mapMoney(yearlyBasePrice, amount => amount / 12n),
        }
      }
      case RecurringSubscriptionInterval.MONTHLY_RECURRING:
        return {
          label: t`Billed monthly`,
          discountPercent: undefined,
          yearlyPrice: mapMoney(monthlyBasePrice, amount => amount * 12n),
          monthlyPrice: monthlyBasePrice,
        }
      case RecurringSubscriptionInterval.NOT_SET_RECURRING:
        throw new Error('invalid interval')
      default:
        return exhaustiveGuard(interval)
    }
  })()

  const yearlyPriceFormatted =
    yearlyPrice &&
    formatPriceConnectWithInterval(
      RecurringSubscriptionInterval.YEARLY_RECURRING,
      yearlyPrice,
      locale,
    )
  const monthlyPriceFormatted =
    monthlyPrice &&
    formatPriceConnectWithInterval(
      RecurringSubscriptionInterval.MONTHLY_RECURRING,
      monthlyPrice,
      locale,
    )

  return (
    <label
      className={cn(
        'relative mt-4 flex flex-col justify-between gap-2 rounded-lg border border-light bg-white p-4 text-grey4',
        {
          'drop-shadow-md hover:cursor-pointer hover:border-signup-radio-button-stroke-hover':
            !checked,
          'bg-signup-background-fill-radio text-primary': checked,
          'rounded-tr-none': discountPercent,
        },
      )}
    >
      <div className="mb-4 flex justify-between">
        <span className="body-sm-sh">{label}</span>
        <input
          name="interval"
          type="radio"
          className="radio focus:outline-none"
          checked={checked}
          onChange={() => setInterval(interval)}
        />
      </div>

      <div className="flex flex-col gap-2">
        <span className="label-md">
          {interval === RecurringSubscriptionInterval.YEARLY_RECURRING
            ? yearlyPriceFormatted
            : monthlyPriceFormatted}
        </span>
        {Boolean(yearlyDiscount) && (
          <span className="body-sm-sh">
            {interval === RecurringSubscriptionInterval.YEARLY_RECURRING
              ? monthlyPriceFormatted
              : yearlyPriceFormatted}
          </span>
        )}
      </div>

      {discountPercent && (
        <div className="label-sm absolute right-[-1px] top-[-1px] translate-y-[-100%] self-end rounded-t-xl bg-good px-2 py-1 text-good">
          <Trans>Save {discountPercent}</Trans>
        </div>
      )}
    </label>
  )
}

function VoucherCodeStatus({
  code,
  offerState,
  confettiCoords,
}: {
  code: string
  offerState: OfferState
  confettiCoords: IRect | undefined
}) {
  const navigate = useNavigate()
  const { setVoucherCode, interval, offer } = offerState
  const closeVoucherHandler = () => setVoucherCode('')
  const voucherCodeStatus = offer?.voucherCodeStatus

  switch (voucherCodeStatus?.case) {
    case undefined:
    case 'couponPossible':
      return undefined
    case 'invalidCode':
      return (
        <FormFieldErrorMessage
          errorMessage={voucherCodeStatus.value.localizedMessage}
        />
      )
    case 'validVoucher':
      return (
        <Modal open onClose={closeVoucherHandler}>
          <VoucherDialog
            onCancel={closeVoucherHandler}
            onSuccess={() => navigate('/web/billing')}
            initialVoucherCode={code}
          />
        </Modal>
      )
    case 'validCoupon':
      return (
        <>
          <FormFieldGoodMessage>
            <Trans>Discount code applied</Trans>
            <div className="grow" />
            <Button
              className="text-black"
              icon={SvgX}
              onClick={() => setVoucherCode('')}
            />
          </FormFieldGoodMessage>
          {!voucherCodeStatus.value.validForIntervals.includes(interval) && (
            <FormFieldErrorMessage
              errorMessage={
                interval === RecurringSubscriptionInterval.MONTHLY_RECURRING
                  ? t`This voucher code only applies to yearly subscription`
                  : t`This voucher code only applies to monthly subscription`
              }
            />
          )}
          {confettiCoords && <Confetti coords={confettiCoords} />}
        </>
      )
    default:
      return exhaustiveGuard(voucherCodeStatus)
  }
}

function VoucherCodeInput({ offerState }: { offerState: OfferState }) {
  const { setVoucherCode, offer, isRefetching } = offerState
  const { coords, takeCoordsFrom } = useConfettiState()
  const componentId = useId()
  const [code, setCode] = useState('')
  const [confettiId, setConfettiId] = useState('')
  const [isOpen, setIsOpen] = useState<string>('closed')

  const voucherCodeStatus = offer?.voucherCodeStatus

  if (!voucherCodeStatus || !voucherCodeStatus.case) {
    return
  }

  return (
    <>
      {voucherCodeStatus.case !== 'validCoupon' && (
        <Accordion
          type="single"
          collapsible
          value={isOpen}
          onValueChange={setIsOpen}
          className="pt-1"
        >
          <AccordionItem value="open" className="border-0">
            <AccordionTrigger className="pr-4">
              <Label className="label-md">
                <Trans>Discount code</Trans>
              </Label>
            </AccordionTrigger>
            <AccordionContent className="border-0" overflowHidden={false}>
              <form
                className="flex gap-4"
                onSubmit={event => {
                  event.preventDefault()
                  setConfettiId(componentId)
                  takeCoordsFrom(event)
                  setVoucherCode(code)
                }}
              >
                <input
                  className="input w-full"
                  value={code}
                  onChange={event => {
                    setVoucherCode('')
                    setCode(event.target.value)
                  }}
                />
                <Button
                  type="submit"
                  className="btn btn-outline"
                  loading={isRefetching}
                  disabled={!code}
                >
                  <Trans>Apply</Trans>
                </Button>
              </form>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}
      <VoucherCodeStatus
        offerState={offerState}
        code={code}
        confettiCoords={confettiId === componentId ? coords : undefined}
      />
    </>
  )
}

export function PaymentInformation({
  offerState,
  successUrl,
  className,
  summary,
  displayCountrySelector,
}: {
  offerState: OfferState
  successUrl: string
  className?: string
  summary?: ReactNode
  displayCountrySelector?: boolean
}) {
  const {
    countryCode,
    setCountryCode,
    paymentMethods,
    currentPaymentInfo,
    extraStorageTB,
    setExtraStorageTB,
    minimumExtraStorage,
    extraSeats,
    setExtraSeats,
    offer,
  } = offerState
  const [useCurrent, setUseCurrent] = useState(false)
  const hasCurrentPaymentMethod = (() => {
    const pm = currentPaymentInfo?.paymentInfo?.paymentMethod.case
    return pm === 'card' || pm === 'vippsBilling' || pm === 'paypal'
  })()

  const [paymentError, setPaymentError] = useState<string>()
  useAndConsumeSearchParam(paymentErrorParam, () => {
    setPaymentError(
      t`Payment failed. Please try again or use another payment method`,
    )
  })

  const paymentOptions = paymentMethods && paymentMethodOptions(paymentMethods)

  const [paymentOption, setPaymentOption] = useState<
    PaymentMethodOption | undefined
  >()

  return (
    <section className={clsx('flex flex-col', className)}>
      <ErrorMessage>{paymentError}</ErrorMessage>

      <div className="grid gap-2 py-12 sm:grid-cols-2">
        <PaymentPlan
          interval={RecurringSubscriptionInterval.YEARLY_RECURRING}
          offerState={offerState}
        />
        <PaymentPlan
          interval={RecurringSubscriptionInterval.MONTHLY_RECURRING}
          offerState={offerState}
        />
      </div>

      {offer?.extraStorageAvailable && (
        <div className="pb-8">
          <Divider />
          <Headline3 className="pb-2">
            <Trans>Extra storage</Trans>
          </Headline3>
          <div className="body-lg-ln flex h-14 items-center rounded-md border border-primary">
            <button
              className="btn-ghost flex h-12 w-12 items-center justify-center"
              disabled={extraStorageTB <= minimumExtraStorage}
              onClick={() => setExtraStorageTB(extraStorageTB - 1)}
            >
              <PlainBrandIcon icon={SvgMinus} height={24} width={24} />
            </button>
            <span className="grow text-center">{`${extraStorageTB} TB`}</span>
            <button
              className="btn-ghost flex h-12 w-12 items-center justify-center"
              onClick={() => setExtraStorageTB(extraStorageTB + 1)}
            >
              <PlainBrandIcon icon={SvgPlus} height={24} width={24} />
            </button>
          </div>
        </div>
      )}

      {offer?.extraSeatsAvailable && (
        <div className="pb-8">
          <Divider />
          <Headline3 className="pb-2">
            <Trans>Extra users</Trans>
          </Headline3>
          <div className="body-lg-ln flex h-14 items-center rounded-md border border-primary">
            <button
              className="btn-ghost flex h-12 w-12 items-center justify-center"
              disabled={extraSeats <= 0}
              onClick={() => setExtraSeats(extraSeats - 1)}
            >
              <PlainBrandIcon icon={SvgMinus} height={24} width={24} />
            </button>
            <span className="grow text-center">{extraSeats}</span>
            <button
              className="btn-ghost flex h-12 w-12 items-center justify-center"
              onClick={() => setExtraSeats(extraSeats + 1)}
            >
              <PlainBrandIcon icon={SvgPlus} height={24} width={24} />
            </button>
          </div>
        </div>
      )}

      <Divider />

      <h3 className="label-lg pb-6 pt-14">
        <Trans>Payment</Trans>
      </h3>

      {displayCountrySelector && (
        <div className="pb-4">
          <Label label={t`Country`}>
            <SelectCountry
              countryCode={countryCode}
              setCountryCode={setCountryCode}
            />
          </Label>
        </div>
      )}

      {hasCurrentPaymentMethod && (
        <PaymentAccordion
          paymentMethods={paymentOptions}
          currentPaymentInfo={currentPaymentInfo}
          setUseCurrent={setUseCurrent}
          setPaymentOption={setPaymentOption}
        />
      )}

      {!hasCurrentPaymentMethod && (
        <PaymentRadio
          paymentMethods={paymentOptions}
          paymentMethod={paymentOption}
          setPaymentMethod={setPaymentOption}
        />
      )}

      {paymentOption !== undefined && (
        <PaymentType
          type={paymentOption}
          offerState={offerState}
          successUrl={successUrl}
          summary={summary}
          useCurrent={useCurrent}
        />
      )}
    </section>
  )
}
